import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from './emailjs-config';
// Deploy trigger - EmailJS variables fix
import './App.css';
import AdvancedBackground from './components/AdvancedBackground';

// Importar imágenes del proyecto de dron
import droneLogo from './img/dron/dronevision-logo.png';
import drone1 from './img/dron/1.png';
import drone2 from './img/dron/2.png';
import drone3 from './img/dron/3.png';
import drone4 from './img/dron/4.png';
import drone5 from './img/dron/5.png';
import drone6 from './img/dron/6.png';
import drone7 from './img/dron/7.png';
import drone8 from './img/dron/8.png';
import drone9 from './img/dron/9.png';
import drone10 from './img/dron/10.png';
import drone11 from './img/dron/11.png';

// Importar imágenes del proyecto Dimol
import dimol1 from './img/dimol1.png';
import dimol2 from './img/dimol2.png';
import dimol3 from './img/dimol3.png';
import dimol4 from './img/dimol4.png';

// Importar otras imágenes
import perfil from './img/LinkedIn.JPG';
import logoDimol from './img/logo-dimol.png';
import logoCs from './img/logo-cs.png';

function App() {
  const { t, i18n } = useTranslation();
  const [darkMode, setDarkMode] = useState(true); // Modo oscuro por defecto
  const [activeSection, setActiveSection] = useState('home');
  const [selectedImage, setSelectedImage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentProjectImages, setCurrentProjectImages] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visibleDates, setVisibleDates] = useState(new Set());
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [showCvModal, setShowCvModal] = useState(false);
  const [cvAction, setCvAction] = useState(null); // 'view' or 'download'
  const [visibleElements, setVisibleElements] = useState(new Set());
  const formRef = useRef();
  const timelineRefs = useRef({});

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const openCvModal = (action) => {
    setCvAction(action);
    setShowCvModal(true);
    document.body.style.overflow = 'hidden';
  };

  const closeCvModal = useCallback(() => {
    setShowCvModal(false);
    setCvAction(null);
    document.body.style.overflow = 'unset';
  }, []);

  const handleCvSelection = (language) => {
    const cvFile = language === 'es' ? '/CV_Matias_Gracia.pdf' : '/Matias_Gracia_CV_EN.pdf';
    const cvName = language === 'es' ? 'CV_Matias_Gracia.pdf' : 'Matias_Gracia_CV_EN.pdf';
    
    if (cvAction === 'view') {
      window.open(cvFile, '_blank', 'noopener,noreferrer');
    } else if (cvAction === 'download') {
      const link = document.createElement('a');
      link.href = cvFile;
      link.download = cvName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    closeCvModal();
  };

  // Cerrar modal con ESC
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (showCvModal && e.key === 'Escape') {
        closeCvModal();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showCvModal, closeCvModal]);

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    setMobileMenuOpen(false);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const openImageModal = (imageSrc, imageAlt, projectImages, imageIndex) => {
    setCurrentProjectImages(projectImages);
    setCurrentImageIndex(imageIndex);
    setSelectedImage({ src: imageSrc, alt: imageAlt });
    setShowModal(true);
    // Prevenir scroll del body cuando el modal está abierto
    document.body.style.overflow = 'hidden';
  };

  const closeImageModal = useCallback(() => {
    setShowModal(false);
    setSelectedImage(null);
    setCurrentImageIndex(0);
    setCurrentProjectImages([]);
    // Restaurar scroll del body
    document.body.style.overflow = 'unset';
  }, []);

  const handleModalClick = (e) => {
    if (e.target === e.currentTarget) {
      closeImageModal();
    }
  };

  const nextImage = useCallback(() => {
    if (currentProjectImages.length > 0) {
      const nextIndex = (currentImageIndex + 1) % currentProjectImages.length;
      const nextImage = currentProjectImages[nextIndex];
      setCurrentImageIndex(nextIndex);
      setSelectedImage({ src: nextImage.src, alt: nextImage.alt });
    }
  }, [currentImageIndex, currentProjectImages]);

  const previousImage = useCallback(() => {
    if (currentProjectImages.length > 0) {
      const prevIndex = currentImageIndex === 0 ? currentProjectImages.length - 1 : currentImageIndex - 1;
      const prevImage = currentProjectImages[prevIndex];
      setCurrentImageIndex(prevIndex);
      setSelectedImage({ src: prevImage.src, alt: prevImage.alt });
    }
  }, [currentImageIndex, currentProjectImages]);



  // Arrays de imágenes por proyecto - Los alt texts se traducen dinámicamente
  // Cuando cambia el idioma, react-i18next re-renderiza el componente automáticamente,
  // por lo que estos arrays se recalculan sin necesidad de i18n.language en las dependencias
  const droneImages = React.useMemo(() => [
    { src: drone1, alt: t('projects.dronevision.images.main') },
    { src: drone2, alt: t('projects.dronevision.images.control') },
    { src: drone3, alt: t('projects.dronevision.images.monitoring') },
    { src: drone4, alt: t('projects.dronevision.images.analysis') },
    { src: drone5, alt: t('projects.dronevision.images.advanced') },
    { src: drone6, alt: t('projects.dronevision.images.dashboard') },
    { src: drone7, alt: t('projects.dronevision.images.navigation') },
    { src: drone8, alt: t('projects.dronevision.images.ui') },
    { src: drone9, alt: t('projects.dronevision.images.sensors') },
    { src: drone10, alt: t('projects.dronevision.images.reports') },
    { src: drone11, alt: t('projects.dronevision.images.flight') }
  ], [t]);

  const dimolImages = React.useMemo(() => [
    { src: dimol1, alt: t('projects.dimol.images.dashboard') },
    { src: dimol2, alt: t('projects.dimol.images.inventory') },
    { src: dimol3, alt: t('projects.dimol.images.production') },
    { src: dimol4, alt: t('projects.dimol.images.reports') }
  ], [t]);

  // Agregar event listener para teclas y limpiar cuando el componente se desmonte
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (showModal) {
        if (e.key === 'Escape') {
          closeImageModal();
        } else if (e.key === 'ArrowRight' && window.innerWidth >= 768) {
          nextImage();
        } else if (e.key === 'ArrowLeft' && window.innerWidth >= 768) {
          previousImage();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Asegurar que el scroll se restaure al desmontar
      document.body.style.overflow = 'unset';
    };
  }, [showModal, currentImageIndex, currentProjectImages, closeImageModal, nextImage, previousImage]);

  // Intersection Observer mejorado para animaciones de entrada
  React.useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const observerOptions = {
      root: null,
      rootMargin: '-10% 0px -10% 0px',
      threshold: [0, 0.1, 0.5, 1]
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const id = entry.target.getAttribute('data-animate-id');
        if (id && entry.isIntersecting) {
          setVisibleElements((prev) => new Set([...prev, id]));
        }
      });
    }, observerOptions);

    // Observar elementos con data-animate-id
    const elementsToObserve = document.querySelectorAll('[data-animate-id]');
    elementsToObserve.forEach((el) => observer.observe(el));

    return () => {
      elementsToObserve.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Intersection Observer para revelar fechas al hacer scroll
  React.useEffect(() => {
    const observers = [];
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -20% 0px',
      threshold: 0.1
    };

    Object.keys(timelineRefs.current).forEach((key) => {
      const element = timelineRefs.current[key];
      if (element) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleDates((prev) => new Set([...prev, key]));
            }
          });
        }, observerOptions);
        
        observer.observe(element);
        observers.push({ observer, element });
      }
    });

    return () => {
      observers.forEach(({ observer, element }) => {
        observer.unobserve(element);
      });
    };
  }, []);

  // Función para manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Función para enviar el formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Enviar email a Matías
      await emailjs.sendForm(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        formRef.current,
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'dark bg-gray-900' : 'bg-gray-50'} relative`}>
      {/* Advanced Background */}
      <div className="fixed inset-0 z-0">
        <AdvancedBackground darkMode={darkMode} />
      </div>
      
      {/* Content wrapper with z-index */}
      <div className="relative z-10">
      {/* Image Modal */}
      {showModal && selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-95 z-[9998] image-modal"
          onClick={handleModalClick}
        >
          {/* Botón cerrar - Siempre visible y fácil de usar */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeImageModal();
            }}
            className="fixed top-4 right-4 sm:top-6 sm:right-6 md:top-8 md:right-8 bg-red-600 text-white font-bold flex items-center justify-center hover:bg-red-700 active:bg-red-800 transition-all duration-200 z-[9999] shadow-2xl border-3 border-white rounded-full w-14 h-14 sm:w-16 sm:h-16 md:w-18 md:h-18 close-button text-xl sm:text-2xl md:text-3xl"
            title={t('modal.close')}
            aria-label={t('modal.closeLabel')}
            style={{ 
              minWidth: '3.5rem',
              minHeight: '3.5rem',
              boxShadow: '0 4px 20px rgba(220, 38, 38, 0.8), 0 0 0 3px rgba(255, 255, 255, 0.3)',
              zIndex: 99999,
              position: 'fixed',
              pointerEvents: 'auto'
            }}
          >
            ✕
          </button>

          {/* Modal para móviles - Layout vertical con scroll */}
          <div 
            className="md:hidden w-full h-full overflow-y-auto relative" 
            onClick={(e) => e.stopPropagation()}
            style={{ touchAction: 'pan-y pinch-zoom' }}
          >
            <div className="flex flex-col items-center py-4 px-2 space-y-8 pb-32">
              {currentProjectImages.map((img, index) => (
                <div 
                  key={index}
                  className="w-full flex flex-col items-center px-2"
                  id={`mobile-image-${index}`}
                >
                  <div 
                    className="w-full flex justify-center image-zoom-container"
                    style={{
                      touchAction: 'pan-x pan-y pinch-zoom',
                      WebkitTouchCallout: 'default',
                      WebkitUserSelect: 'none',
                      userSelect: 'none'
                    }}
                  >
                    <img
                      src={img.src}
                      alt={img.alt}
                      className="w-full max-w-full h-auto object-contain rounded-lg shadow-2xl zoomable-image"
                      style={{ 
                        maxHeight: '80vh',
                        display: 'block',
                        touchAction: 'pan-x pan-y pinch-zoom',
                        WebkitUserDrag: 'none',
                        userSelect: 'none'
                      }}
                      loading="lazy"
                      draggable="false"
                    />
                  </div>
                  <div className="mt-4 text-center px-2 w-full max-w-md mx-auto">
                    <p className={`text-sm font-semibold ${
                      darkMode ? 'text-gray-300' : 'text-white'
                    }`}>
                      {img.alt}
                    </p>
                    <p className={`text-xs mt-2 image-counter inline-block px-3 py-1 rounded-full ${
                      darkMode ? 'text-gray-400 bg-gray-800' : 'text-gray-300 bg-gray-900'
                    }`}>
                      {index + 1} {t('modal.of')} {currentProjectImages.length}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Modal para desktop/tablet - Layout tradicional */}
          <div className="hidden md:flex items-center justify-center w-full h-full">
            <div className="relative w-full h-full flex items-center justify-center px-4">
              {/* Botón anterior */}
              {currentProjectImages.length > 1 && (
                <button
                  onClick={previousImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-70 hover:bg-opacity-90 text-white p-4 rounded-full modal-nav-button z-20 text-3xl lg:text-4xl transition-all duration-200"
                  aria-label={t('modal.previousImage')}
                >
                  ‹
                </button>
              )}

              {/* Imagen principal */}
              <div className="flex flex-col items-center max-w-5xl w-full">
                <img
                  src={selectedImage.src}
                  alt={selectedImage.alt}
                  className="w-auto h-auto object-contain rounded-lg shadow-2xl max-w-full"
                  style={{ 
                    maxHeight: '85vh',
                    maxWidth: '90vw'
                  }}
                />
                <div className="mt-4 text-center px-4">
                  <p className={`text-base lg:text-lg font-semibold ${
                    darkMode ? 'text-gray-300' : 'text-white'
                  }`}>
                    {selectedImage.alt}
                  </p>
                  {currentProjectImages.length > 1 && (
                    <p className={`text-sm mt-2 image-counter ${
                      darkMode ? 'text-gray-400' : 'text-gray-300'
                    }`}>
                      {currentImageIndex + 1} {t('modal.of')} {currentProjectImages.length}
                    </p>
                  )}
                </div>
              </div>

              {/* Botón siguiente */}
              {currentProjectImages.length > 1 && (
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-70 hover:bg-opacity-90 text-white p-4 rounded-full modal-nav-button z-20 text-3xl lg:text-4xl transition-all duration-200"
                  aria-label={t('modal.nextImage')}
                >
                  ›
                </button>
              )}

              {/* Indicadores de navegación */}
              {currentProjectImages.length > 1 && (
                <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
                  {currentProjectImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setCurrentImageIndex(index);
                        setSelectedImage({ src: currentProjectImages[index].src, alt: currentProjectImages[index].alt });
                      }}
                      className={`w-3 h-3 rounded-full modal-indicator transition-all duration-200 ${
                        index === currentImageIndex 
                          ? 'bg-white scale-125' 
                          : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                      }`}
                      aria-label={t('modal.goToImage', { index: index + 1 })}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CV Language Selection Modal */}
      {showCvModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-[9998] flex items-center justify-center p-4"
          onClick={closeCvModal}
        >
          <div 
            className={`max-w-md w-full rounded-xl shadow-2xl p-6 ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className={`text-2xl font-bold ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {t('cv.modal.title')}
              </h3>
              <button
                onClick={closeCvModal}
                className={`p-2 rounded-lg transition-colors duration-200 ${
                  darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                }`}
                aria-label={t('cv.modal.close')}
              >
                ✕
              </button>
            </div>
            
            <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {cvAction === 'view' 
                ? t('cv.modal.view')
                : t('cv.modal.download')
              } - {t('cv.modal.title')}
            </p>

            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => handleCvSelection('es')}
                className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                  darkMode
                    ? 'border-gray-600 bg-gray-700 hover:bg-gray-600 text-white'
                    : 'border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-900'
                } hover:border-indigo-500 hover:shadow-lg`}
              >
                <div className="text-4xl mb-2">🇪🇸</div>
                <div className="font-semibold">{t('cv.modal.spanish')}</div>
                <div className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  CV_Matias_Gracia.pdf
                </div>
              </button>
              
              <button
                onClick={() => handleCvSelection('en')}
                className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                  darkMode
                    ? 'border-gray-600 bg-gray-700 hover:bg-gray-600 text-white'
                    : 'border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-900'
                } hover:border-indigo-500 hover:shadow-lg`}
              >
                <div className="text-4xl mb-2">🇺🇸</div>
                <div className="font-semibold">{t('cv.modal.english')}</div>
                <div className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Matias_Gracia_CV_EN.pdf
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header/Navigation */}
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        darkMode ? 'bg-gray-900/95 backdrop-blur-sm' : 'bg-white/95 backdrop-blur-sm'
      } shadow-lg relative`}>
        <nav className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="text-xl sm:text-2xl font-bold text-indigo-600">MG</div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex space-x-6 lg:space-x-8">
              {['home', 'about', 'timeline', 'projects', 'skills', 'frameworks', 'cv', 'contact'].map((section) => (
                <button
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className={`capitalize transition-colors duration-200 text-sm lg:text-base ${
                    activeSection === section
                      ? 'text-indigo-600 font-semibold'
                      : darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {t(`navigation.${section}`)}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              {/* Language Toggle Button */}
              <button
                onClick={() => changeLanguage(i18n.language === 'es' ? 'en' : 'es')}
                className={`p-2 rounded-lg transition-colors duration-200 language-toggle ${
                  darkMode ? 'bg-gray-800 text-gray-300 hover:text-white' : 'bg-gray-100 text-gray-600 hover:text-gray-900'
                }`}
                title={i18n.language === 'es' ? 'Switch to English' : 'Cambiar a Español'}
                aria-label={i18n.language === 'es' ? 'Switch to English' : 'Cambiar a Español'}
              >
                {i18n.language === 'es' ? '🇺🇸' : '🇪🇸'}
              </button>

              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-lg transition-colors duration-200 dark-mode-toggle ${
                  darkMode ? 'bg-gray-800 text-yellow-400' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {darkMode ? '☀️' : '🌙'}
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`md:hidden p-2 rounded-lg transition-colors duration-200 ${
                  darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {mobileMenuOpen ? '✕' : '☰'}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className={`md:hidden mt-4 pb-4 border-t ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className="flex flex-col space-y-2 pt-4">
                {['home', 'about', 'timeline', 'projects', 'skills', 'frameworks', 'cv', 'contact'].map((section) => (
                  <button
                    key={section}
                    onClick={() => scrollToSection(section)}
                    className={`capitalize transition-colors duration-200 text-left px-4 py-2 rounded-lg ${
                      activeSection === section
                        ? 'text-indigo-600 font-semibold bg-indigo-50 dark:bg-indigo-900/20'
                        : darkMode ? 'text-gray-300 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    {t(`navigation.${section}`)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-20 sm:pt-24 pb-12 sm:pb-16 px-4 sm:px-6 hero-section relative z-10">
        <div className="container mx-auto text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 sm:mb-6 hero-title ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {t('hero.title')}
            </h1>
            <h2 className={`text-xl sm:text-2xl md:text-3xl font-semibold mb-6 sm:mb-8 hero-subtitle ${
              darkMode ? 'text-indigo-400' : 'text-indigo-600'
            }`}>
              {t('hero.subtitle')}
            </h2>
            <p className={`text-base sm:text-lg md:text-xl mb-8 sm:mb-12 max-w-2xl mx-auto hero-description ${
              darkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {t('hero.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center hero-buttons">
              <button
                onClick={() => scrollToSection('projects')}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold transition-colors duration-200 btn-primary text-sm sm:text-base"
              >
                {t('hero.viewProjects')}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className={`border-2 border-indigo-600 px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold transition-colors duration-200 btn-primary text-sm sm:text-base ${
                  darkMode 
                    ? 'text-indigo-400 border-indigo-400 hover:bg-indigo-400 hover:text-gray-900' 
                    : 'text-indigo-600 hover:bg-indigo-600 hover:text-white'
                }`}
              >
                {t('hero.contact')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 px-6 relative">
        <div className="container mx-auto">
          <h2 
            data-animate-id="about-title"
            className={`text-4xl font-bold text-center mb-16 ${
              darkMode ? 'text-white' : 'text-gray-900'
            } ${visibleElements.has('about-title') ? 'animate-reveal-up' : 'opacity-0'}`}
          >
            {t('about.title')}
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:text-left">
              <div 
                data-animate-id="about-image"
                className={`w-64 h-64 rounded-full mx-auto md:mx-0 mb-8 shadow-xl profile-image-container overflow-hidden ${
                  visibleElements.has('about-image') ? 'animate-reveal-scale' : 'opacity-0'
                }`}
              >
                <img 
                  src={perfil} 
                  alt="Matías Gracia" 
                  className="w-full h-full object-cover profile-image"
                  style={{
                    objectPosition: 'center 25%',
                    transform: 'scale(1.4)',
                    transformOrigin: 'center center'
                  }}
                />
              </div>
            </div>
            <div>
              <p className={`text-lg mb-6 ${
                darkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {t('about.paragraph1')}
              </p>
              <p className={`text-lg mb-6 ${
                darkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {t('about.paragraph2')}
              </p>
              <p className={`text-lg mb-6 ${
                darkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
              dangerouslySetInnerHTML={{ __html: t('about.paragraph3') }}
              />
              <div className="flex flex-wrap gap-4">
                <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                  darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                }`}>
                  {t('about.tags.reactNode')}
                </div>
                <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                  darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                }`}>
                  {t('about.tags.pythonDjango')}
                </div>
                <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                  darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                }`}>
                  {t('about.tags.embedded')}
                </div>
                <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                  darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                }`}>
                  {t('about.tags.iotDrones')}
                </div>
                <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                  darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                }`}>
                  {t('about.tags.gitAzure')}
                </div>
                <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                  darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                }`}>
                  {t('about.tags.agileScrum')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section id="timeline" className="py-16 px-6 relative z-10">
        <div className="container mx-auto">
          <h2 className={`text-4xl font-bold text-center mb-16 ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {t('timeline.title')}
          </h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Línea vertical con fechas dinámicas */}
              <div className={`absolute left-4 sm:left-6 md:left-8 top-0 bottom-0 w-0.5 sm:w-1 timeline-line-animated ${
                darkMode ? 'bg-gray-700' : 'bg-gray-300'
              }`}></div>


              {/* Proyectos en la línea de tiempo */}
              <div className="space-y-8 sm:space-y-10 md:space-y-12">
                {/* Proyecto 1 - BDO Chile (Más reciente) */}
                <div 
                  ref={(el) => { if (el) timelineRefs.current['bdo'] = el; }}
                  className="relative flex items-start timeline-item"
                >
                  {/* Fecha en la línea - Izquierda */}
                  <div className={`absolute left-4 sm:left-6 md:left-8 transform -translate-x-full -translate-y-1/2 timeline-date ${visibleDates.has('bdo') ? 'timeline-date-visible' : ''}`} style={{ left: '0' }}>
                    <div className={`whitespace-nowrap ${
                      darkMode ? 'text-indigo-400' : 'text-indigo-600'
                    } font-semibold text-xs sm:text-sm`}>
                      <div className={`flex flex-col items-end px-2 py-1 rounded-lg ${
                        darkMode ? 'bg-gray-800/90' : 'bg-white/90'
                      } shadow-lg backdrop-blur-sm`}>
                        <span>{t('timeline.months.august')}</span>
                        <span>2025</span>
                      </div>
                    </div>
                  </div>
                  <div className={`absolute left-2 sm:left-4 md:left-6 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 sm:border-4 timeline-dot ${
                    darkMode ? 'bg-indigo-600 border-gray-800' : 'bg-indigo-600 border-white'
                  }`} style={{ zIndex: 3 }}></div>
                  <div className="ml-8 sm:ml-12 md:ml-16 flex-1">
                    <div className={`p-4 sm:p-5 md:p-6 rounded-xl shadow-lg ${
                      darkMode ? 'bg-gray-800' : 'bg-white'
                    }`}>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
                        <h3 className={`text-xl sm:text-2xl font-bold ${
                          darkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          BDO Chile
                        </h3>
                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium self-start sm:self-auto ${
                          darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'
                        }`}>
                          {t('timeline.current')}
                        </span>
                      </div>
                      <p className={`text-base sm:text-lg mb-3 sm:mb-4 ${
                        darkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {t('timeline.projects.bdo.description')}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {t('timeline.projects.bdo.tags', { returnObjects: true }).map((tag, idx) => (
                          <span key={idx} className={`px-3 py-1 rounded-full text-sm ${
                            idx === 0 ? 'bg-blue-100 text-blue-800' :
                            idx === 1 ? 'bg-purple-100 text-purple-800' :
                            idx === 2 ? 'bg-green-100 text-green-800' :
                            'bg-orange-100 text-orange-800'
                          }`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Proyecto 2 - DroneVision Platform */}
                <div 
                  ref={(el) => { if (el) timelineRefs.current['drone'] = el; }}
                  className="relative flex items-start timeline-item"
                >
                  {/* Fecha en la línea - Izquierda */}
                  <div className={`absolute left-4 sm:left-6 md:left-8 transform -translate-x-full -translate-y-1/2 timeline-date ${visibleDates.has('drone') ? 'timeline-date-visible' : ''}`} style={{ left: '0' }}>
                    <div className={`whitespace-nowrap ${
                      darkMode ? 'text-indigo-400' : 'text-indigo-600'
                    } font-semibold text-xs sm:text-sm`}>
                      <div className={`flex flex-col items-end px-2 py-1 rounded-lg ${
                        darkMode ? 'bg-gray-800/90' : 'bg-white/90'
                      } shadow-lg backdrop-blur-sm`}>
                        <span>{t('timeline.months.march')}</span>
                        <span>2025</span>
                      </div>
                    </div>
                  </div>
                  <div className={`absolute left-2 sm:left-4 md:left-6 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 sm:border-4 timeline-dot ${
                    darkMode ? 'bg-indigo-600 border-gray-800' : 'bg-indigo-600 border-white'
                  }`} style={{ zIndex: 3 }}></div>
                  <div className="ml-8 sm:ml-12 md:ml-16 flex-1">
                    <div className={`p-4 sm:p-5 md:p-6 rounded-xl shadow-lg ${
                      darkMode ? 'bg-gray-800' : 'bg-white'
                    }`}>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
                        <h3 className={`text-xl sm:text-2xl font-bold ${
                          darkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          DroneVision Platform
                        </h3>
                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium self-start sm:self-auto ${
                          darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {t('timeline.completed')}
                        </span>
                      </div>
                      <p className={`text-base sm:text-lg mb-3 sm:mb-4 ${
                        darkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {t('timeline.projects.dronevision.description')}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {t('timeline.projects.dronevision.tags', { returnObjects: true }).map((tag, idx) => (
                          <span key={idx} className={`px-3 py-1 rounded-full text-sm ${
                            idx === 0 ? 'bg-blue-100 text-blue-800' :
                            idx === 1 ? 'bg-green-100 text-green-800' :
                            idx === 2 ? 'bg-purple-100 text-purple-800' :
                            idx === 3 ? 'bg-orange-100 text-orange-800' :
                            idx === 4 ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Proyecto 3 - Sistema Dimol */}
                <div 
                  ref={(el) => { if (el) timelineRefs.current['dimol'] = el; }}
                  className="relative flex items-start timeline-item"
                >
                  {/* Fecha en la línea - Izquierda */}
                  <div className={`absolute left-4 sm:left-6 md:left-8 transform -translate-x-full -translate-y-1/2 timeline-date ${visibleDates.has('dimol') ? 'timeline-date-visible' : ''}`} style={{ left: '0' }}>
                    <div className={`whitespace-nowrap ${
                      darkMode ? 'text-purple-400' : 'text-purple-600'
                    } font-semibold text-xs sm:text-sm`}>
                      <div className={`flex flex-col items-end px-2 py-1 rounded-lg ${
                        darkMode ? 'bg-gray-800/90' : 'bg-white/90'
                      } shadow-lg backdrop-blur-sm`}>
                        <span>{t('timeline.months.january')}</span>
                        <span>2025</span>
                      </div>
                    </div>
                  </div>
                  <div className={`absolute left-2 sm:left-4 md:left-6 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 sm:border-4 ${
                    darkMode ? 'bg-purple-600 border-gray-800' : 'bg-purple-600 border-white'
                  }`}></div>
                  <div className="ml-8 sm:ml-12 md:ml-16 flex-1">
                    <div className={`p-4 sm:p-5 md:p-6 rounded-xl shadow-lg ${
                      darkMode ? 'bg-gray-800' : 'bg-white'
                    }`}>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
                        <h3 className={`text-xl sm:text-2xl font-bold ${
                          darkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          Sistema Dimol
                        </h3>
                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium self-start sm:self-auto ${
                          darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {t('timeline.completed')}
                        </span>
                      </div>
                      <p className={`text-base sm:text-lg mb-3 sm:mb-4 ${
                        darkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {t('timeline.projects.dimol.description')}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {t('timeline.projects.dimol.tags', { returnObjects: true }).map((tag, idx) => (
                          <span key={idx} className={`px-3 py-1 rounded-full text-sm ${
                            idx === 0 ? 'bg-blue-100 text-blue-800' :
                            idx === 1 ? 'bg-green-100 text-green-800' :
                            idx === 2 ? 'bg-purple-100 text-purple-800' :
                            idx === 3 ? 'bg-orange-100 text-orange-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Proyecto 4 - CarStation (Más antiguo) */}
                <div 
                  ref={(el) => { if (el) timelineRefs.current['car'] = el; }}
                  className="relative flex items-start timeline-item"
                >
                  {/* Fecha en la línea - Izquierda */}
                  <div className={`absolute left-4 sm:left-6 md:left-8 transform -translate-x-full -translate-y-1/2 timeline-date ${visibleDates.has('car') ? 'timeline-date-visible' : ''}`} style={{ left: '0' }}>
                    <div className={`whitespace-nowrap ${
                      darkMode ? 'text-blue-400' : 'text-blue-600'
                    } font-semibold text-xs sm:text-sm`}>
                      <div className={`flex flex-col items-end px-2 py-1 rounded-lg ${
                        darkMode ? 'bg-gray-800/90' : 'bg-white/90'
                      } shadow-lg backdrop-blur-sm`}>
                        <span>{t('timeline.months.june')}</span>
                        <span>2024</span>
                      </div>
                    </div>
                  </div>
                  <div className={`absolute left-2 sm:left-4 md:left-6 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 sm:border-4 ${
                    darkMode ? 'bg-blue-600 border-gray-800' : 'bg-blue-600 border-white'
                  }`}></div>
                  <div className="ml-8 sm:ml-12 md:ml-16 flex-1">
                    <div className={`p-4 sm:p-5 md:p-6 rounded-xl shadow-lg ${
                      darkMode ? 'bg-gray-800' : 'bg-white'
                    }`}>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
                        <h3 className={`text-xl sm:text-2xl font-bold ${
                          darkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          CarStation
                        </h3>
                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium self-start sm:self-auto ${
                          darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {t('timeline.completed')}
                        </span>
                      </div>
                      <p className={`text-base sm:text-lg mb-3 sm:mb-4 ${
                        darkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {t('timeline.projects.carstation.description')}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {t('timeline.projects.carstation.tags', { returnObjects: true }).map((tag, idx) => (
                          <span key={idx} className={`px-3 py-1 rounded-full text-sm ${
                            idx === 0 ? 'bg-blue-100 text-blue-800' :
                            idx === 1 ? 'bg-green-100 text-green-800' :
                            idx === 2 ? 'bg-purple-100 text-purple-800' :
                            idx === 3 ? 'bg-orange-100 text-orange-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-16 px-6 relative z-10">
        <div className="container mx-auto">
          <h2 className={`text-4xl font-bold text-center mb-16 ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {t('projects.title')}
          </h2>
          
          {/* DroneVision Platform */}
          <div className="mb-20">
            <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center mb-8 md:mb-12">
              <div className="order-2 lg:order-1">
                <div className="flex items-center mb-4 md:mb-6">
                  <div className="project-logo">
                    <img src={droneLogo} alt="DroneVision Logo" className="w-12 h-12 sm:w-16 sm:h-16 mr-3 sm:mr-4 object-contain" />
                  </div>
                  <h3 className={`text-2xl sm:text-3xl font-bold ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    DroneVision Platform
                  </h3>
                </div>
                <p className={`text-lg mb-6 ${
                  darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {t('projects.dronevision.description')}
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {t('timeline.projects.dronevision.tags', { returnObjects: true }).map((tag, idx) => (
                    <span key={idx} className={`px-3 py-1 rounded-full text-sm ${
                      idx === 0 ? 'bg-blue-100 text-blue-800' :
                      idx === 1 ? 'bg-green-100 text-green-800' :
                      idx === 2 ? 'bg-purple-100 text-purple-800' :
                      idx === 3 ? 'bg-orange-100 text-orange-800' :
                      idx === 4 ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="space-y-2">
                  <h4 className={`font-semibold ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {t('projects.dronevision.features.title')}
                  </h4>
                  <ul className={`space-y-1 ${
                    darkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {t('projects.dronevision.features.items', { returnObjects: true }).map((feature, idx) => (
                      <li key={idx}>• {feature}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 order-1 lg:order-2">
                <img 
                  src={drone1} 
                  alt={t('projects.dronevision.images.main')} 
                  className="w-full h-auto rounded-lg shadow-lg drone-image cursor-pointer object-cover" 
                  onClick={() => openImageModal(drone1, t('projects.dronevision.images.main'), droneImages, 0)}
                />
                <img 
                  src={drone2} 
                  alt={t('projects.dronevision.images.control')} 
                  className="w-full h-auto rounded-lg shadow-lg drone-image cursor-pointer object-cover" 
                  onClick={() => openImageModal(drone2, t('projects.dronevision.images.control'), droneImages, 1)}
                />
                <img 
                  src={drone3} 
                  alt={t('projects.dronevision.images.monitoring')} 
                  className="w-full h-auto rounded-lg shadow-lg drone-image cursor-pointer object-cover" 
                  onClick={() => openImageModal(drone3, t('projects.dronevision.images.monitoring'), droneImages, 2)}
                />
                <img 
                  src={drone4} 
                  alt={t('projects.dronevision.images.analysis')} 
                  className="w-full h-auto rounded-lg shadow-lg drone-image cursor-pointer object-cover" 
                  onClick={() => openImageModal(drone4, t('projects.dronevision.images.analysis'), droneImages, 3)}
                />
              </div>
            </div>
            
            {/* Gallery */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3 md:gap-4">
              {[
                { src: drone5, altKey: 'projects.dronevision.images.advanced' },
                { src: drone6, altKey: 'projects.dronevision.images.dashboard' },
                { src: drone7, altKey: 'projects.dronevision.images.navigation' },
                { src: drone8, altKey: 'projects.dronevision.images.ui' },
                { src: drone9, altKey: 'projects.dronevision.images.sensors' },
                { src: drone10, altKey: 'projects.dronevision.images.reports' },
                { src: drone11, altKey: 'projects.dronevision.images.flight' }
              ].map((img, index) => {
                const altText = t(img.altKey);
                return (
                  <img 
                    key={index}
                    src={img.src} 
                    alt={altText} 
                    className="w-full h-auto rounded-lg shadow-md hover:shadow-xl transition-shadow duration-200 cursor-pointer drone-image object-cover"
                    onClick={() => openImageModal(img.src, altText, droneImages, index + 4)}
                  />
                );
              })}
            </div>
          </div>

          {/* Sistema Dimol */}
          <div className="mb-20">
            <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center mb-8 md:mb-12">
              <div className="order-2 lg:order-1">
                <div className="flex items-center mb-4 md:mb-6">
                  <div className="project-logo">
                    <img src={logoDimol} alt="Dimol Logo" className="w-12 h-12 sm:w-16 sm:h-16 mr-3 sm:mr-4 object-contain" />
                  </div>
                  <h3 className={`text-2xl sm:text-3xl font-bold ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    Sistema Dimol
                  </h3>
                </div>
                <p className={`text-lg mb-6 whitespace-pre-line ${
                  darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {t('projects.dimol.description')}
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {t('timeline.projects.dimol.tags', { returnObjects: true }).map((tag, idx) => (
                    <span key={idx} className={`px-3 py-1 rounded-full text-sm ${
                      idx === 0 ? 'bg-blue-100 text-blue-800' :
                      idx === 1 ? 'bg-green-100 text-green-800' :
                      idx === 2 ? 'bg-purple-100 text-purple-800' :
                      idx === 3 ? 'bg-orange-100 text-orange-800' :
                      idx === 4 ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="space-y-2">
                  <h4 className={`font-semibold ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {t('projects.dimol.features.title')}
                  </h4>
                  <ul className={`space-y-1 ${
                    darkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {t('projects.dimol.features.items', { returnObjects: true }).map((feature, idx) => (
                      <li key={idx}>• {feature}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 order-1 lg:order-2">
                <img 
                  src={dimol1} 
                  alt={t('projects.dimol.images.dashboard')} 
                  className="w-full h-auto rounded-lg shadow-lg drone-image cursor-pointer object-cover" 
                  onClick={() => openImageModal(dimol1, t('projects.dimol.images.dashboard'), dimolImages, 0)}
                />
                <img 
                  src={dimol2} 
                  alt={t('projects.dimol.images.inventory')} 
                  className="w-full h-auto rounded-lg shadow-lg drone-image cursor-pointer object-cover" 
                  onClick={() => openImageModal(dimol2, t('projects.dimol.images.inventory'), dimolImages, 1)}
                />
                <img 
                  src={dimol3} 
                  alt={t('projects.dimol.images.production')} 
                  className="w-full h-auto rounded-lg shadow-lg drone-image cursor-pointer object-cover" 
                  onClick={() => openImageModal(dimol3, t('projects.dimol.images.production'), dimolImages, 2)}
                />
                <img 
                  src={dimol4} 
                  alt={t('projects.dimol.images.reports')} 
                  className="w-full h-auto rounded-lg shadow-lg drone-image cursor-pointer object-cover" 
                  onClick={() => openImageModal(dimol4, t('projects.dimol.images.reports'), dimolImages, 3)}
                />
              </div>
            </div>
          </div>

          {/* CS Project */}
          <div className="mb-20">
            <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="order-2 lg:order-2">
                <div className="flex items-center mb-4 md:mb-6">
                  <div className="project-logo">
                    <img src={logoCs} alt="CS Logo" className="w-12 h-12 sm:w-16 sm:h-16 mr-3 sm:mr-4 object-contain" />
                  </div>
                  <h3 className={`text-2xl sm:text-3xl font-bold ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    CarStation
                  </h3>
                </div>
                <p className={`text-lg mb-6 ${
                  darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {t('projects.carstation.description')}
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {t('timeline.projects.carstation.tags', { returnObjects: true }).map((tag, idx) => (
                    <span key={idx} className={`px-3 py-1 rounded-full text-sm ${
                      idx === 0 ? 'bg-blue-100 text-blue-800' :
                      idx === 1 ? 'bg-green-100 text-green-800' :
                      idx === 2 ? 'bg-purple-100 text-purple-800' :
                      idx === 3 ? 'bg-orange-100 text-orange-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="space-y-2">
                  <h4 className={`font-semibold ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {t('projects.carstation.features.title')}
                  </h4>
                  <ul className={`space-y-1 ${
                    darkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {t('projects.carstation.features.items', { returnObjects: true }).map((feature, idx) => (
                      <li key={idx}>• {feature}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="order-1 lg:order-1">
                <div className={`p-4 sm:p-6 md:p-8 rounded-xl shadow-lg ${
                  darkMode ? 'bg-gray-800' : 'bg-white'
                }`}>
                  <div className="text-center">
                    <div className="project-logo">
                      <img src={logoCs} alt="CarStation Logo" className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 mx-auto mb-3 sm:mb-4 object-contain" />
                    </div>
                    <h4 className={`text-lg sm:text-xl font-bold mb-3 sm:mb-4 ${
                      darkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {t('projects.carstation.completed')}
                    </h4>
                    <p className={`text-sm sm:text-base mb-4 sm:mb-6 ${
                      darkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {t('projects.carstation.completedDescription')}
                    </p>
                    <div className={`px-3 sm:px-4 py-2 rounded-lg ${
                      darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <span className="text-xs sm:text-sm">{t('projects.carstation.finished')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-16 px-6 relative z-10">
        <div className="container mx-auto">
          <h2 className={`text-4xl font-bold text-center mb-16 ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {t('skills.title')}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Frontend */}
            <div 
              data-animate-id="skill-frontend"
              className={`p-6 rounded-xl shadow-lg skill-card ${
                darkMode ? 'bg-gray-800' : 'bg-white'
              } ${visibleElements.has('skill-frontend') ? 'animate-reveal-up' : 'opacity-0'}`}
            >
              <h3 className={`text-2xl font-bold mb-6 text-center ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {t('skills.frontend')}
              </h3>
              <div className="space-y-4">
                {[
                  { name: t('skills.frontendSkills.react'), level: t('skills.levels.intermediateAdvanced') },
                  { name: t('skills.frontendSkills.vuejs'), level: t('skills.levels.intermediateAdvanced') },
                  { name: t('skills.frontendSkills.javascript'), level: t('skills.levels.intermediateAdvanced') },
                  { name: t('skills.frontendSkills.typescript'), level: t('skills.levels.intermediateAdvanced') },
                  { name: t('skills.frontendSkills.tailwind'), level: t('skills.levels.intermediateAdvanced') },
                  { name: t('skills.frontendSkills.htmlCss'), level: t('skills.levels.intermediateAdvanced') }
                ].map((skill, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between mb-2">
                      <span className={`font-medium ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {skill.name}
                      </span>
                      <span className={`text-sm px-2 py-1 rounded-full ${
                        skill.level === t('skills.levels.intermediateAdvanced')
                          ? 'bg-blue-100 text-blue-800' 
                          : skill.level === t('skills.levels.intermediate')
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {skill.level}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Backend */}
            <div 
              data-animate-id="skill-backend"
              className={`p-6 rounded-xl shadow-lg skill-card ${
                darkMode ? 'bg-gray-800' : 'bg-white'
              } ${visibleElements.has('skill-backend') ? 'animate-reveal-up' : 'opacity-0'}`}
            >
              <h3 className={`text-2xl font-bold mb-6 text-center ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {t('skills.backend')}
              </h3>
              <div className="space-y-4">
                {[
                  { name: t('skills.backendSkills.nodejs'), level: t('skills.levels.intermediateAdvanced') },
                  { name: t('skills.backendSkills.python'), level: t('skills.levels.intermediateAdvanced') },
                  { name: t('skills.backendSkills.php'), level: t('skills.levels.intermediateAdvanced') },
                  { name: t('skills.backendSkills.django'), level: t('skills.levels.intermediateAdvanced') },
                  { name: t('skills.backendSkills.express'), level: t('skills.levels.intermediateAdvanced') },
                  { name: t('skills.backendSkills.laravel'), level: t('skills.levels.intermediateAdvanced') },
                  { name: t('skills.backendSkills.restApis'), level: t('skills.levels.intermediateAdvanced') }
                ].map((skill, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between mb-2">
                      <span className={`font-medium ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {skill.name}
                      </span>
                      <span className={`text-sm px-2 py-1 rounded-full ${
                        skill.level === t('skills.levels.intermediateAdvanced')
                          ? 'bg-green-100 text-green-800' 
                          : skill.level === t('skills.levels.intermediate')
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {skill.level}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Especialidades */}
            <div 
              data-animate-id="skill-specialties"
              className={`p-6 rounded-xl shadow-lg skill-card ${
                darkMode ? 'bg-gray-800' : 'bg-white'
              } ${visibleElements.has('skill-specialties') ? 'animate-reveal-up' : 'opacity-0'}`}
            >
              <h3 className={`text-2xl font-bold mb-6 text-center ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {t('skills.specialties')}
              </h3>
              <div className="space-y-4">
                {[
                  { name: t('skills.specialtySkills.embedded'), level: t('skills.levels.intermediate') },
                  { name: t('skills.specialtySkills.iotDrones'), level: t('skills.levels.intermediateAdvanced') },
                  { name: t('skills.specialtySkills.arduino'), level: t('skills.levels.intermediate') },
                  { name: t('skills.specialtySkills.webrtc'), level: t('skills.levels.intermediate') },
                  { name: t('skills.specialtySkills.machineLearning'), level: t('skills.levels.intermediateAdvanced') },
                  { name: t('skills.specialtySkills.devops'), level: t('skills.levels.intermediateAdvanced') },
                  { name: t('skills.specialtySkills.git'), level: t('skills.levels.intermediateAdvanced') },
                  { name: t('skills.specialtySkills.azureDevops'), level: t('skills.levels.intermediateAdvanced') },
                  { name: t('skills.specialtySkills.agileScrum'), level: t('skills.levels.intermediateAdvanced') }
                ].map((skill, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between mb-2">
                      <span className={`font-medium ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {skill.name}
                      </span>
                      <span className={`text-sm px-2 py-1 rounded-full ${
                        skill.level === t('skills.levels.intermediateAdvanced')
                          ? 'bg-purple-100 text-purple-800' 
                          : skill.level === t('skills.levels.intermediate')
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {skill.level}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Frameworks Section */}
      <section id="frameworks" className="py-16 px-6 relative z-10">
        <div className="container mx-auto">
          <h2 className={`text-4xl font-bold text-center mb-16 ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {t('frameworks.title')}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Frontend Frameworks */}
            <div className={`p-6 rounded-xl shadow-lg skill-card ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <h3 className={`text-2xl font-bold mb-6 text-center ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {t('frameworks.frontend')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl">⚛️</span>
                  </div>
                  <p className={`font-medium text-sm ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('frameworks.technologies.react')}
                  </p>
                  <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full mt-1">
                    {t('frameworks.categories.frontend')}
                  </span>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl">🟢</span>
                  </div>
                  <p className={`font-medium text-sm ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('frameworks.technologies.vuejs')}
                  </p>
                  <span className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full mt-1">
                    {t('frameworks.categories.frontend')}
                  </span>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl">🎨</span>
                  </div>
                  <p className={`font-medium text-sm ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('frameworks.technologies.tailwind')}
                  </p>
                  <span className="inline-block px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full mt-1">
                    {t('frameworks.categories.css')}
                  </span>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl">📱</span>
                  </div>
                  <p className={`font-medium text-sm ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('frameworks.technologies.responsive')}
                  </p>
                  <span className="inline-block px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full mt-1">
                    {t('frameworks.categories.uiUx')}
                  </span>
                </div>
              </div>
            </div>

            {/* Backend Frameworks */}
            <div className={`p-6 rounded-xl shadow-lg skill-card ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <h3 className={`text-2xl font-bold mb-6 text-center ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {t('frameworks.backend')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl">🟢</span>
                  </div>
                  <p className={`font-medium text-sm ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('frameworks.technologies.nodejs')}
                  </p>
                  <span className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full mt-1">
                    {t('frameworks.categories.backend')}
                  </span>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl">🐍</span>
                  </div>
                  <p className={`font-medium text-sm ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('frameworks.technologies.django')}
                  </p>
                  <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full mt-1">
                    {t('frameworks.categories.backend')}
                  </span>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl">🐘</span>
                  </div>
                  <p className={`font-medium text-sm ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('frameworks.technologies.laravel')}
                  </p>
                  <span className="inline-block px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full mt-1">
                    {t('frameworks.categories.backend')}
                  </span>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl">⚡</span>
                  </div>
                  <p className={`font-medium text-sm ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('frameworks.technologies.express')}
                  </p>
                  <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full mt-1">
                    {t('frameworks.categories.backend')}
                  </span>
                </div>
              </div>
            </div>

            {/* Databases & Cloud */}
            <div className={`p-6 rounded-xl shadow-lg skill-card ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <h3 className={`text-2xl font-bold mb-6 text-center ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {t('frameworks.databasesCloud')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl">🗄️</span>
                  </div>
                  <p className={`font-medium text-sm ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('frameworks.technologies.mysql')}
                  </p>
                  <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full mt-1">
                    {t('frameworks.categories.database')}
                  </span>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl">☁️</span>
                  </div>
                  <p className={`font-medium text-sm ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('frameworks.technologies.aws')}
                  </p>
                  <span className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full mt-1">
                    {t('frameworks.categories.cloud')}
                  </span>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl">📊</span>
                  </div>
                  <p className={`font-medium text-sm ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('frameworks.technologies.dynamodb')}
                  </p>
                  <span className="inline-block px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full mt-1">
                    {t('frameworks.categories.nosql')}
                  </span>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl">🔧</span>
                  </div>
                  <p className={`font-medium text-sm ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('frameworks.technologies.mongodb')}
                  </p>
                  <span className="inline-block px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full mt-1">
                    {t('frameworks.categories.nosql')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CV Download Section */}
      <section id="cv" className="py-16 px-6 relative z-10">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className={`text-4xl font-bold mb-8 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {t('cv.title')}
            </h2>
            <p className={`text-xl mb-8 max-w-2xl mx-auto ${
              darkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {t('cv.description')}
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => openCvModal('download')}
                className="inline-flex items-center px-8 py-4 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl cv-download-button bg-indigo-600 hover:bg-indigo-700"
              >
                <span className="mr-3 text-2xl">📄</span>
                <span>{t('cv.download')}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 px-6 relative z-10">
        <div className="container mx-auto">
          <h2 className={`text-4xl font-bold text-center mb-16 ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {t('contact.title')}
          </h2>
          
          <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-12">
            {/* Información de Contacto */}
            <div className="lg:col-span-1">
              <h3 className={`text-2xl font-bold mb-6 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {t('contact.workTogether')}
              </h3>
              <p className={`text-lg mb-8 ${
                darkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {t('contact.description')}
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white">📧</span>
                  </div>
                  <div>
                    <p className={`font-semibold ${
                      darkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {t('contact.fields.email')}
                    </p>
                    <a 
                      href="mailto:matiasgraciadelpino@gmail.com"
                      className={`hover:text-indigo-600 transition-colors duration-200 ${
                        darkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}
                    >
                      matiasgraciadelpino@gmail.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white">📱</span>
                  </div>
                  <div>
                    <p className={`font-semibold ${
                      darkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {t('contact.fields.phone')}
                    </p>
                    <a 
                      href="tel:+56983400342"
                      className={`hover:text-indigo-600 transition-colors duration-200 ${
                        darkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}
                    >
                      +56 9 8340 0342
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white">📍</span>
                  </div>
                  <div>
                    <p className={`font-semibold ${
                      darkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {t('contact.fields.location')}
                    </p>
                    <p className={`${
                      darkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {t('contact.fields.locationValue')}
                    </p>
                  </div>
                </div>

                {/* Redes Sociales */}
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white">🔗</span>
                  </div>
                  <div>
                    <p className={`font-semibold mb-2 ${
                      darkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {t('contact.fields.socialNetworks')}
                    </p>
                    <div className="flex space-x-4">
                      <a 
                        href="https://github.com/matias-gracia" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={`hover:text-indigo-600 transition-colors duration-200 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}
                      >
                        GitHub
                      </a>
                      <a 
                        href="https://www.linkedin.com/in/matias-gracia-del-pino-630590235/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={`hover:text-indigo-600 transition-colors duration-200 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}
                      >
                        LinkedIn
                      </a>
                    </div>
                  </div>
                </div>

                {/* CV */}
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white">📄</span>
                  </div>
                  <div>
                    <p className={`font-semibold mb-2 ${
                      darkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {t('contact.fields.resume')}
                    </p>
                    <div className="flex space-x-4">
                      <button
                        onClick={() => openCvModal('view')}
                        className={`hover:text-green-600 transition-colors duration-200 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        } cursor-pointer`}
                      >
                        {t('contact.fields.viewCv')}
                      </button>
                      <button
                        onClick={() => openCvModal('download')}
                        className={`hover:text-green-600 transition-colors duration-200 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        } cursor-pointer`}
                      >
                        {t('contact.fields.downloadCv')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Formulario de Contacto */}
            <div className={`p-8 rounded-xl shadow-lg ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('contact.fields.name')}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-3 rounded-lg border contact-input ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
                    placeholder={t('contact.fields.namePlaceholder')}
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('contact.fields.email')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-3 rounded-lg border contact-input ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
                    placeholder={t('contact.fields.emailPlaceholder')}
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('contact.fields.message')}
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    className={`w-full px-4 py-3 rounded-lg border contact-input ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
                    placeholder={t('contact.fields.messagePlaceholder')}
                  ></textarea>
                </div>

                {/* Mensajes de estado */}
                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg form-status-message">
                    ✅ {t('contact.form.success')}
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg form-status-message">
                    ❌ {t('contact.form.error')}
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors duration-200 btn-primary ${
                    isSubmitting 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <span className="animate-spin mr-2">⏳</span>
                      {t('contact.form.sending')}
                    </span>
                  ) : (
                    t('contact.form.submit')
                  )}
                </button>
              </form>
            </div>

            {/* Calendario de Reservas */}
            <div className={`p-8 rounded-xl shadow-lg ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <h3 className={`text-2xl font-bold mb-6 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {t('contact.schedule.title')}
              </h3>
              <p className={`text-lg mb-6 ${
                darkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {t('contact.schedule.description')}
              </p>
              
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${
                  darkMode ? 'bg-gray-700' : 'bg-gray-50'
                }`}>
                  <h4 className={`font-semibold mb-2 ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {t('contact.schedule.consultation.title')}
                  </h4>
                  <p className={`text-sm mb-3 ${
                    darkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {t('contact.schedule.consultation.duration')}
                  </p>
                  <ul className={`text-sm space-y-1 ${
                    darkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {t('contact.schedule.consultation.items', { returnObjects: true }).map((item, idx) => (
                      <li key={idx}>• {item}</li>
                    ))}
                  </ul>
                </div>

                <a
                  href="https://calendly.com/matias_gracia/45min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-lg font-semibold text-center transition-colors duration-200"
                >
                  {t('contact.schedule.consultation.button')}
                </a>

                <div className={`p-4 rounded-lg ${
                  darkMode ? 'bg-gray-700' : 'bg-gray-50'
                }`}>
                  <h4 className={`font-semibold mb-2 ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {t('contact.schedule.meeting.title')}
                  </h4>
                  <p className={`text-sm mb-3 ${
                    darkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {t('contact.schedule.meeting.duration')}
                  </p>
                  <ul className={`text-sm space-y-1 ${
                    darkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {t('contact.schedule.meeting.items', { returnObjects: true }).map((item, idx) => (
                      <li key={idx}>• {item}</li>
                    ))}
                  </ul>
                </div>

                <a
                  href="https://calendly.com/matias_gracia/45min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-semibold text-center transition-colors duration-200"
                >
                  {t('contact.schedule.meeting.button')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-8 px-6 relative z-10 ${
        darkMode ? 'bg-gray-900 text-gray-400' : 'bg-gray-100 text-gray-600'
      }`}>
        <div className="container mx-auto text-center">
          <p>{t('footer.copyright')}</p>
          <p className="mt-2">{t('footer.developed')}</p>
        </div>
      </footer>
      </div>
    </div>
  );
}

export default App;
